// =============================================================================
// Origin UI — src/pages/creditcards/CreditCardApprovalsPage.tsx
// Shows CardStatementReviews pending the current user's approval.
// Expandable rows with full transaction detail. Approve and Return actions.
// CC-13.1 through CC-13.4 (+ sort, header fixes, bank column)
// =============================================================================

import { useState, useEffect, useMemo } from 'react';
import { PageHeader, Card, ErrorBanner, LoadingSpinner } from '@/components/common';
import {
  getPendingApprovals, getReviewDetail, approveReview, returnReview, getMyDelegations,
  type CardStatementReviewListItem, type CardStatementReview, type DelegationTarget
} from '@/services/creditCardApi';
import { api } from '@/services/apiService';
import styles from './creditcards.module.css';

type SortKey = 'cardholderName' | 'cardIssuer' | 'periodName' | 'totalAmount' | 'transactionCount' | 'submittedAt';
type SortDir = 'asc' | 'desc';

export function CreditCardApprovalsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reviews, setReviews] = useState<CardStatementReviewListItem[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedDetail, setExpandedDetail] = useState<CardStatementReview | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [returnModalId, setReturnModalId] = useState<string | null>(null);
  const [returnNotes, setReturnNotes] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Delegate filter
  const [delegations, setDelegations] = useState<DelegationTarget[]>([]);
  const [myEmployeeId, setMyEmployeeId] = useState<string>('');
  const [viewingAs, setViewingAs] = useState<string>('all');

  // Sort
  const [sortKey, setSortKey] = useState<SortKey>('submittedAt');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      const [r, d, me] = await Promise.all([
        getPendingApprovals(),
        getMyDelegations().catch((): DelegationTarget[] => []),
        api.get<{ employeeId: string }>('/api/employees/me').catch(() => null)
      ]);
      setReviews(r);
      setDelegations(d);
      if (me) setMyEmployeeId(me.employeeId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const sortedReviews = useMemo(() => {
    return [...reviews].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'totalAmount' || sortKey === 'transactionCount') {
        cmp = (a[sortKey] || 0) - (b[sortKey] || 0);
      } else if (sortKey === 'submittedAt') {
        cmp = (a.submittedAt || '').localeCompare(b.submittedAt || '');
      } else {
        cmp = (a[sortKey] || '').localeCompare(b[sortKey] || '');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [reviews, sortKey, sortDir]);

  const filteredReviews = useMemo(() => {
    if (viewingAs === 'all') return sortedReviews;
    if (viewingAs === 'mine') return sortedReviews.filter(r => r.approverEmployeeId === myEmployeeId);
    return sortedReviews.filter(r => r.approverEmployeeId === viewingAs);
  }, [sortedReviews, viewingAs, myEmployeeId]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  function sortIcon(key: SortKey) {
    if (sortKey !== key) return ' \u21D5';
    return sortDir === 'asc' ? ' \u2191' : ' \u2193';
  }

  async function toggleExpand(id: string) {
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    try {
      setLoadingDetail(true);
      setExpandedId(id);
      const detail = await getReviewDetail(id);
      setExpandedDetail(detail);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingDetail(false);
    }
  }

  async function handleApprove(id: string) {
    try {
      setActionLoading(true);
      await approveReview(id);
      setSuccessMsg('Review approved.');
      setTimeout(() => setSuccessMsg(''), 3000);
      setExpandedId(null);
      setExpandedDetail(null);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReturn() {
    if (!returnModalId || !returnNotes.trim()) return;
    try {
      setActionLoading(true);
      await returnReview(returnModalId, { notes: returnNotes });
      setSuccessMsg('Review returned to cardholder.');
      setTimeout(() => setSuccessMsg(''), 3000);
      setReturnModalId(null);
      setReturnNotes('');
      setExpandedId(null);
      setExpandedDetail(null);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader title="Credit Card Approvals" subtitle="Review and approve cardholder statements" />

      {delegations.length > 0 && (
        <div style={{ marginBottom: 16, padding: '10px 16px', background: 'var(--turquoise-light)', borderRadius: 8, border: '1px solid var(--turquoise)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--turquoise-dark)' }}>Viewing:</label>
          <select style={{ fontSize: 13, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--turquoise)', background: 'white', color: 'var(--navy)' }} value={viewingAs} onChange={e => setViewingAs(e.target.value)}>
            <option value="all">All ({reviews.length})</option>
            <option value="mine">My Reviews ({reviews.filter(r => r.approverEmployeeId === myEmployeeId).length})</option>
            {delegations.map(d => (
              <option key={d.employeeId} value={d.employeeId}>
                {d.fullName}'s Reviews ({reviews.filter(r => r.approverEmployeeId === d.employeeId).length})
              </option>
            ))}
          </select>
        </div>
      )}

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {successMsg && (
        <div style={{ padding: '10px 16px', marginBottom: 16, borderRadius: 6, background: 'var(--green-light)', color: 'var(--green)', fontSize: 13, fontWeight: 600 }}>
          {successMsg}
        </div>
      )}

      {reviews.length === 0 ? (
        <Card><p style={{ padding: 20, textAlign: 'center', color: 'var(--gray-500)' }}>No card statements pending your approval.</p></Card>
      ) : (
        <Card padding={false}>
          <table className={styles.txTable}>
            <thead>
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('cardholderName')}>Cardholder{sortIcon('cardholderName')}</th>
                <th>Card</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('cardIssuer')}>Bank{sortIcon('cardIssuer')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('periodName')}>Period{sortIcon('periodName')}</th>
                <th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('totalAmount')}>Total Spend{sortIcon('totalAmount')}</th>
                <th style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleSort('transactionCount')}>Transactions{sortIcon('transactionCount')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('submittedAt')}>Submitted{sortIcon('submittedAt')}</th>
                <th style={{ width: 160 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReviews.map(r => (
                <ReviewRow
                  key={r.cardStatementReviewId}
                  review={r}
                  isExpanded={expandedId === r.cardStatementReviewId}
                  detail={expandedId === r.cardStatementReviewId ? expandedDetail : null}
                  loadingDetail={loadingDetail && expandedId === r.cardStatementReviewId}
                  actionLoading={actionLoading}
                  onToggle={() => toggleExpand(r.cardStatementReviewId)}
                  onApprove={() => handleApprove(r.cardStatementReviewId)}
                  onReturn={() => { setReturnModalId(r.cardStatementReviewId); setReturnNotes(''); }}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Return modal */}
      {returnModalId && (
        <div className={styles.modalOverlay} onClick={() => setReturnModalId(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Return for Review</h3>
            <p style={{ marginBottom: 12, color: 'var(--gray-600)', fontSize: 14 }}>
              Provide notes explaining what needs to be updated. The cardholder will see this message.
            </p>
            <textarea
              className={styles.returnNotesInput}
              value={returnNotes}
              onChange={e => setReturnNotes(e.target.value)}
              placeholder="Enter notes for the cardholder..."
              rows={4}
            />
            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={() => setReturnModalId(null)}>Cancel</button>
              <button
                className={styles.returnBtn}
                onClick={handleReturn}
                disabled={!returnNotes.trim() || actionLoading}
              >
                {actionLoading ? 'Returning...' : 'Return'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Review Row with expandable detail ──────────────────────────────────────

function ReviewRow({ review, isExpanded, detail, loadingDetail, actionLoading, onToggle, onApprove, onReturn }: {
  review: CardStatementReviewListItem;
  isExpanded: boolean;
  detail: CardStatementReview | null;
  loadingDetail: boolean;
  actionLoading: boolean;
  onToggle: () => void;
  onApprove: () => void;
  onReturn: () => void;
}) {
  return (
    <>
      <tr className={styles.txRow} onClick={onToggle} style={{ cursor: 'pointer' }}>
        <td style={{ fontWeight: 600 }}>{review.cardholderName}</td>
        <td>{'\u2022\u2022\u2022\u2022'} {review.cardLastFourDigits}</td>
        <td>{review.cardIssuer}</td>
        <td>{review.periodName}</td>
        <td style={{ textAlign: 'right', fontWeight: 600 }}>{review.cardCurrency} {review.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
        <td style={{ textAlign: 'center' }}>{review.transactionCount}</td>
        <td>{review.submittedAt ? new Date(review.submittedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '\u2014'}</td>
        <td>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className={styles.approveBtn} onClick={e => { e.stopPropagation(); onApprove(); }} disabled={actionLoading}>Approve</button>
            <button className={styles.returnActionBtn} onClick={e => { e.stopPropagation(); onReturn(); }} disabled={actionLoading}>Return</button>
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={8} style={{ padding: 0, background: 'var(--gray-50)' }}>
            {loadingDetail ? (
              <div style={{ padding: 20, textAlign: 'center' }}><LoadingSpinner /></div>
            ) : detail ? (
              <div className={styles.expandedDetail}>
                <table className={styles.txTable} style={{ margin: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 90 }}>Date</th>
                      <th>Merchant</th>
                      <th style={{ width: 130 }}>Category</th>
                      <th style={{ width: 100, textAlign: 'right' }}>Amount</th>
                      <th>Commentary</th>
                      <th style={{ width: 70, textAlign: 'center' }}>Personal</th>
                      <th style={{ width: 70, textAlign: 'center' }}>Disputed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.transactions.map(tx => (
                      <tr key={tx.cardTransactionId}>
                        <td className={styles.txDate}>{new Date(tx.transactionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</td>
                        <td>{tx.merchantName}</td>
                        <td className={styles.txCategory}>{tx.merchantCategory || '\u2014'}</td>
                        <td className={styles.txAmount} style={{ color: tx.amount < 0 ? 'var(--green)' : undefined }}>
                          {tx.amount < 0 ? '-' : ''}{tx.currency} {Math.abs(tx.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td>{tx.commentary || <span style={{ color: 'var(--gray-400)' }}>{'\u2014'}</span>}</td>
                        <td style={{ textAlign: 'center' }}>{tx.isFlaggedPersonal ? '\u26A0\uFE0F' : ''}</td>
                        <td style={{ textAlign: 'center' }}>{tx.isFlaggedDisputed ? '\u26A0\uFE0F' : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </td>
        </tr>
      )}
    </>
  );
}
